import { useEffect } from 'react';
import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface PerformanceMetrics {
  cls: number | null;
  inp: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
}

/**
 * Performance monitoring component for Core Web Vitals
 * Tracks and reports performance metrics
 */
export function PerformanceMonitor() {
  useEffect(() => {
    const metrics: PerformanceMetrics = {
      cls: null,
      inp: null,
      fcp: null,
      lcp: null,
      ttfb: null,
    };

    const sendToAnalytics = (metric: Metric) => {
      // Update metrics
      switch (metric.name) {
        case 'CLS':
          metrics.cls = metric.value;
          break;
        case 'INP':
          metrics.inp = metric.value;
          break;
        case 'FCP':
          metrics.fcp = metric.value;
          break;
        case 'LCP':
          metrics.lcp = metric.value;
          break;
        case 'TTFB':
          metrics.ttfb = metric.value;
          break;
      }

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 ${metric.name}:`, metric.value, metric.rating);
      }

      // Send to analytics service (Google Analytics, etc.)
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', metric.name, {
          event_category: 'Web Vitals',
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          event_label: metric.id,
          non_interaction: true,
        });
      }

      // Check if all metrics are collected
      const allMetricsCollected = Object.values(metrics).every(value => value !== null);
      if (allMetricsCollected) {
        reportPerformanceSummary(metrics);
      }
    };

    // Collect Core Web Vitals
    onCLS(sendToAnalytics);
    onINP(sendToAnalytics);
    onFCP(sendToAnalytics);
    onLCP(sendToAnalytics);
    onTTFB(sendToAnalytics);

    // Additional performance monitoring
    if ('PerformanceObserver' in window) {
      // Monitor long tasks
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              console.warn('⚠️ Long task detected:', `${entry.duration  }ms`);
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch {
        // Long task API not supported
      }

      // Monitor layout shifts
      try {
        const layoutShiftObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if ('value' in entry && typeof entry.value === 'number' && entry.value > 0.1) {
              console.warn('⚠️ Large layout shift detected:', entry.value);
            }
          }
        });
        layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
      } catch {
        // Layout shift API not supported
      }
    }
  }, []);

  return null; // This component doesn't render anything
}

/**
 * Report performance summary when all metrics are collected
 */
function reportPerformanceSummary(metrics: PerformanceMetrics) {
  const summary = {
    lcp: metrics.lcp,
    inp: metrics.inp,
    cls: metrics.cls,
    fcp: metrics.fcp,
    ttfb: metrics.ttfb,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  // Log summary in development
  if (process.env.NODE_ENV === 'development') {
    console.group('📊 Performance Summary');
    console.log('LCP (Largest Contentful Paint):', `${metrics.lcp  }ms`, getPerformanceRating(metrics.lcp!, 'lcp'));
    console.log('INP (Interaction to Next Paint):', `${metrics.inp  }ms`, getPerformanceRating(metrics.inp!, 'inp'));
    console.log('CLS (Cumulative Layout Shift):', metrics.cls, getPerformanceRating(metrics.cls!, 'cls'));
    console.log('FCP (First Contentful Paint):', `${metrics.fcp  }ms`, getPerformanceRating(metrics.fcp!, 'fcp'));
    console.log('TTFB (Time to First Byte):', `${metrics.ttfb  }ms`, getPerformanceRating(metrics.ttfb!, 'ttfb'));
    console.groupEnd();
  }

  // Send to monitoring service
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'performance_summary', {
      event_category: 'Performance',
      custom_parameters: summary,
    });
  }
}

/**
 * Get performance rating based on Core Web Vitals thresholds
 */
function getPerformanceRating(value: number, metric: string): string {
  const thresholds = {
    lcp: { good: 2500, poor: 4000 },
    inp: { good: 200, poor: 500 },
    cls: { good: 0.1, poor: 0.25 },
    fcp: { good: 1800, poor: 3000 },
    ttfb: { good: 800, poor: 1800 },
  };

  const threshold = thresholds[metric as keyof typeof thresholds];
  if (!threshold) return 'unknown';

  if (value <= threshold.good) return '✅ Good';
  if (value <= threshold.poor) return '⚠️ Needs Improvement';
  return '❌ Poor';
}

export default PerformanceMonitor;
